/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import Divider from '@material-ui/core/Divider';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';

import useStyles from './MapDialogLayoutStyles';

import { useLoadScript } from '@react-google-maps/api';
import Map from './components/Map';

import { mdiMap, mdiVectorSquareRemove } from '@mdi/js';
import Icon from '@mdi/react';
import useCircles from './hooks/useCircles';
import useMarkers from './hooks/useMarkers';
import usePolygons from './hooks/usePolygons';

function MapDialogLayout(props) {
  const {
    projects,
    records,
    onClose,
    mapType,

    selectPositionMode,
    handleSelectPosition,
    defaultSelectedPosition,

    drawCircleMode,
    defaultRadius,
    handleCircleRadius,

    drawAreaMode,
    defaultArea,
    handleDrawArea,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('common');
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [selectedPosition, setSelectedPosition] = useState(
    defaultSelectedPosition
  );

  const [circleRadius, setCircleRadius] = useState(defaultRadius || 0);

  const [drawnAreaPositions, setDrawnAreaPositions] = useState(
    defaultArea || []
  );

  const onResetArea = () => {
    setDrawnAreaPositions([]);
  };

  const handleSelect = () => {
    selectPositionMode && handleSelectPosition(selectedPosition);
    drawCircleMode && handleCircleRadius(Math.round(circleRadius));
    drawAreaMode && handleDrawArea(drawnAreaPositions);
    onClose();
  };

  const { allMarkers } = useMarkers({
    records,
    projects,
    selectedPosition,
    selectPositionMode,
  });

  const { allCircles } = useCircles({
    projects,
    selectedPosition,
    circleRadius,
  });

  const { allPolygons } = usePolygons({
    projects,
    drawAreaMode,
    drawnAreaPositions,
  });

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  return (
    <Dialog
      aria-labelledby="contact-edit-dialog"
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick
      fullScreen={!upSm}
      fullWidth
      maxWidth="sm"
      open={true}
      onClose={onClose}
    >
      <DialogHeader
        icon={<Icon path={mdiMap} size={1} className={classes.icon} />}
        onClose={onClose}
        title={t('location.map')}
      />

      <DialogContent className={classes.dialogContent}>
        {isLoaded && (
          <Map
            mapType={mapType}
            markers={allMarkers}
            circles={allCircles}
            polygons={allPolygons}
            selectPositionMode={selectPositionMode}
            setSelectedPosition={setSelectedPosition}
            drawCircleMode={drawCircleMode}
            setCircleRadius={setCircleRadius}
            drawAreaMode={drawAreaMode}
            setAreaPositions={setDrawnAreaPositions}
          />
        )}
      </DialogContent>

      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.actionBtn}
          onClick={onClose}
          color="dark"
          outline
        >
          Sulje
        </Button>
        {drawAreaMode && (
          <Button
            className={classes.actionBtn}
            onClick={onResetArea}
            color="danger"
            disabled={drawnAreaPositions.length === 0}
          >
            <Icon path={mdiVectorSquareRemove} size={1} />
            Tyhjää alue
          </Button>
        )}
        {(selectPositionMode || drawAreaMode || drawCircleMode) && (
          <Button
            className={classes.actionBtn}
            onClick={() => handleSelect()}
            color="dark"
            disabled={
              !selectedPosition ||
              (drawAreaMode && drawnAreaPositions.length < 3)
            }
          >
            Valitse
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

MapDialogLayout.defaultProps = {};

MapDialogLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  records: PropTypes.array,
  projects: PropTypes.array,
  mapType: PropTypes.string,
};

export default MapDialogLayout;
